import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ChartLineIcon from 'mdi-react/ChartLineIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import { arrayOf, bool, func, object, string } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { I18n } from 'utils/i18n';

const getStyles = theme => ({
  paper: { borderRadius: '16px', maxWidth: 640, width: '100%' },
  title: {
    padding: theme.spacing(2, 3, 1.5, 3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleText: { fontSize: theme.typography.pxToRem(18), fontWeight: 700 },
  content: {
    padding: theme.spacing(0, 3, 3, 3),
    '&.MuiDialogContent-root': { paddingTop: theme.spacing(1) },
  },
  toolbar: { display: 'flex', gap: theme.spacing(1.5), marginBottom: theme.spacing(2) },
  search: { flex: 1 },
  createBtn: { textTransform: 'none', whiteSpace: 'nowrap' },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxHeight: 480,
    overflowY: 'auto',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '12px',
    backgroundColor: theme.palette.background.paper,
  },
  iconWrap: {
    width: 32,
    height: 32,
    borderRadius: '8px',
    backgroundColor: theme.palette.background.default,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  name: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  emptyState: {
    padding: theme.spacing(5, 2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(13),
  },
});

const ManageMetricsDialog = ({
  open,
  metrics = [],
  attachedIds = [],
  onClose,
  onSaveAttachments,
  onCreateMetric,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [search, setSearch] = useState('');
  const [localIds, setLocalIds] = useState([]);

  useEffect(() => {
    if (open) {
      setLocalIds((attachedIds || []).map(String));
      setSearch('');
    }
  }, [open, attachedIds]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return metrics;
    return metrics.filter(
      m =>
        (m.name || '').toLowerCase().includes(q) || (m.description || '').toLowerCase().includes(q),
    );
  }, [metrics, search]);

  const toggle = id => {
    const sid = String(id);
    setLocalIds(prev => (prev.includes(sid) ? prev.filter(x => x !== sid) : [...prev, sid]));
  };

  const dirty = useMemo(() => {
    const base = (attachedIds || []).map(String).sort().join('|');
    const cur = [...localIds].sort().join('|');
    return base !== cur;
  }, [attachedIds, localIds]);

  const handleClose = () => {
    if (dirty) onSaveAttachments(localIds);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: styles.paper } }}
    >
      <DialogTitle sx={styles.title} disableTypography>
        <Typography component="span" sx={styles.titleText}>
          {I18n.t('AgenticChatFlow.MetricsManageTitle')}
        </Typography>
        <IconButton size="small" onClick={handleClose} aria-label={I18n.t('Common.close')}>
          <CloseIcon size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.content}>
        <Box sx={styles.toolbar}>
          <TextField
            sx={styles.search}
            size="small"
            placeholder={I18n.t('AgenticChatFlow.MetricsSearchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <MagnifyIcon size={18} />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            sx={styles.createBtn}
            startIcon={<PlusIcon size={16} />}
            onClick={() => {
              if (dirty) onSaveAttachments(localIds);
              onCreateMetric();
            }}
          >
            {I18n.t('AgenticChatFlow.MetricsCreate')}
          </Button>
        </Box>
        {filtered.length === 0 && (
          <Box sx={styles.emptyState}>{I18n.t('AgenticChatFlow.MetricsManageEmpty')}</Box>
        )}
        <Box sx={styles.list}>
          {filtered.map(m => {
            const attached = localIds.includes(String(m._id));
            return (
              <Box key={m._id} sx={styles.row}>
                <Box component="span" sx={styles.iconWrap}>
                  <ChartLineIcon size={16} />
                </Box>
                <Typography sx={[styles.name, { flex: 1, minWidth: 0 }]} noWrap>
                  {m.name}
                </Typography>
                {attached ? (
                  <Button
                    variant="outlined"
                    size="small"
                    color="secondary"
                    onClick={() => toggle(m._id)}
                  >
                    {I18n.t('AgenticChatFlow.Detach')}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    disableElevation
                    onClick={() => toggle(m._id)}
                  >
                    {I18n.t('AgenticChatFlow.Attach')}
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

ManageMetricsDialog.propTypes = {
  open: bool.isRequired,
  metrics: arrayOf(object),
  attachedIds: arrayOf(string),
  onClose: func.isRequired,
  onSaveAttachments: func.isRequired,
  onCreateMetric: func.isRequired,
};

export default ManageMetricsDialog;
